"use client";

import { useState } from "react";

import { useClickOutside } from "@/hooks/useClickOutside";
import { GroupedSortSelectProps } from "@/types/search.types";
import { cn } from "@/utils/cn";

export const GroupedSortSelect = ({
  value,
  groupedOptions,
  onChange,
  className,
}: GroupedSortSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useClickOutside(() => setIsOpen(false));

  // Find current selected option label
  const currentLabel = (() => {
    for (const group of groupedOptions) {
      const option = group.options.find((opt) => opt.value === value);
      if (option) {
        return option.label;
      }
    }
    return groupedOptions[0]?.options[0]?.label || "";
  })();

  const handleOptionSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "relative cursor-pointer",
        "h-[4.8rem] lg:h-[5.4rem] min-w-[16rem] lg:min-w-[20rem]",
        "px-4 lg:px-6",
        "bg-white border border-gray-100 rounded-2xl",
        "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]",
        "flex items-center justify-between",
        "text-[1.4rem] font-normal text-gray-900",
        "hover:border-gray-200 transition-colors",
        className,
      )}
      onClick={() => setIsOpen(!isOpen)}
    >
      {/* Current Selection */}
      <span className="truncate">{currentLabel}</span>

      {/* Arrow Icon */}
      <div
        className={cn(
          "ml-2 w-5 h-2.5 transition-transform",
          "bg-red-500",
          "mask-[url('/assets/icons/ui/forms/select-arrow-red.svg')] mask-no-repeat mask-center",
          isOpen && "rotate-180",
        )}
        style={{
          maskImage: "url('/assets/icons/ui/forms/select-arrow-red.svg')",
        }}
      />

      {/* Dropdown */}
      {isOpen && (
        <div
          className={cn(
            "absolute top-full right-0 mt-1 z-50",
            "bg-white border border-gray-100 rounded-xl shadow-lg",
            "max-h-[520px] w-fit overflow-y-auto",
            "p-6",
          )}
        >
          {groupedOptions.map((group, groupIndex) => (
            <div key={groupIndex}>
              {/* Group Separator */}
              {groupIndex > 0 && <div className="h-px bg-gray-100 mx-4 my-2" />}

              {/* Group Header */}
              <div className="px-4 py-2 text-sm font-bold text-[#323232]">
                {group.groupLabel}
              </div>

              {/* Group Options */}
              {group.options.map((option) => (
                <button
                  key={option.value}
                  className={cn(
                    "w-full px-4 py-2 text-left text-sm text-[#323232] whitespace-nowrap",
                    "hover:bg-gray-50 transition-colors",
                    "flex items-center justify-between",
                    option.value === value &&
                      "bg-red-50 font-medium",
                  )}
                  onClick={() => handleOptionSelect(option.value)}
                >
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
